import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Etiqueta } from "@/components/ui/etiqueta";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  useCuerpoDeRevision,
  useExperimentos,
  useGuardarBorrador,
  usePromptsDelLaboratorio,
  usePublicarPrompt,
  useRevisionesDelPrompt,
} from "@/hooks/useLaboratorio";
import { getApiErrorMessage } from "@/lib/api";
import { formatearFecha } from "@/lib/fechas";
import {
  agruparPorFase,
  diffDeLineas,
  estadoDeLaPuerta,
  etiquetaDeRevision,
  type PromptDelLaboratorio,
} from "@/lib/laboratorio";
import { cn } from "@/lib/utils";

/** Lo que se espera sin escribir antes de guardar el borrador. */
export const RETRASO_DEL_AUTOGUARDADO = 800;

const BORRADOR = "borrador" as const;

/**
 * Pestaña «Prompts»: la lista agrupada por fase y, al lado, el detalle del
 * elegido con su borrador, las versiones publicadas, la puerta de publicación
 * y el editor.
 */
export function PromptsDelLaboratorio({ onProbar }: { onProbar: (funcionId: number) => void }) {
  const catalogo = usePromptsDelLaboratorio();
  const [elegidoId, setElegidoId] = useState<number | null>(null);

  const prompts = useMemo(() => catalogo.data?.data ?? [], [catalogo.data]);
  const grupos = useMemo(() => agruparPorFase(prompts, catalogo.data?.fases ?? []), [prompts, catalogo.data]);
  const elegido = prompts.find((p) => p.id === elegidoId) ?? grupos[0]?.prompts[0] ?? null;

  if (catalogo.isPending) {
    return (
      <div className="grid gap-3.5 lg:grid-cols-[18.125rem_minmax(0,1fr)]">
        <Skeleton className="h-96 rounded-[1.25rem]" />
        <Skeleton className="h-96 rounded-[1.25rem]" />
      </div>
    );
  }

  if (catalogo.isError) {
    return <p className="text-meta text-danger-texto">No pudimos cargar los prompts.</p>;
  }

  return (
    <div className="grid items-start gap-3.5 lg:grid-cols-[18.125rem_minmax(0,1fr)]">
      <Card variant="marcada" className="gap-2.5 rounded-[1.25rem] p-2.5">
        <ul aria-label="Prompts" className="flex flex-col gap-2.5">
          {grupos.map((grupo) => (
            <li key={grupo.fase} className="flex flex-col gap-0.5">
              <span className="rotulo-seccion px-2 py-1">{grupo.fase}</span>
              <ul className="flex flex-col gap-0.5">
                {grupo.prompts.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => setElegidoId(p.id)}
                      aria-current={elegido?.id === p.id ? "true" : undefined}
                      className={cn(
                        "flex min-h-10 w-full items-center gap-2 rounded-[0.625rem] border-[1.5px] border-transparent px-2.5 py-1.5 text-left transition-colors hover:bg-superficie-tenue",
                        elegido?.id === p.id && "border-input bg-divisor hover:bg-divisor",
                      )}
                    >
                      <span className="min-w-0 flex-1 truncate text-[0.78125rem] font-bold">{p.nombre}</span>
                      {p.tiene_borrador && p.admite_experimentos ? (
                        <Etiqueta tono="info" size="sm" className="text-[0.5625rem]">
                          BORRADOR
                        </Etiqueta>
                      ) : null}
                      <span className="text-ink-muted font-mono text-[0.65625rem] font-bold">{p.version_publicada ?? "—"}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </Card>

      {elegido ? <DetalleDelPrompt key={elegido.id} prompt={elegido} onProbar={onProbar} /> : null}
    </div>
  );
}

function DetalleDelPrompt({ prompt, onProbar }: { prompt: PromptDelLaboratorio; onProbar: (id: number) => void }) {
  const revisiones = useRevisionesDelPrompt(prompt.id);
  const experimentos = useExperimentos(prompt.id);
  const guardar = useGuardarBorrador();
  const publicar = usePublicarPrompt();

  const [version, setVersion] = useState<typeof BORRADOR | number>(BORRADOR);
  const [verCambios, setVerCambios] = useState(false);
  const [editado, setEditado] = useState<string | null>(null);
  const [errorDePublicacion, setErrorDePublicacion] = useState<string | null>(null);

  const lista = useMemo(() => revisiones.data ?? [], [revisiones.data]);
  const enUso = useCuerpoDeRevision(prompt.id, prompt.revision_en_uso_id);
  const publicadaElegida = useCuerpoDeRevision(prompt.id, version === BORRADOR ? null : version);

  const textoBase = prompt.borrador_texto ?? enUso.data?.cuerpo ?? "";
  const texto = editado ?? textoBase;
  const esBorrador = version === BORRADOR;
  const editable = esBorrador && prompt.admite_experimentos && !verCambios;

  const etiquetaDelBorrador = lista.length > 0 ? `v${lista.length}` : null;
  const ultimoExperimento = (experimentos.data ?? []).find((e) => e.revision_id === prompt.borrador_revision_id) ?? null;
  const puerta = estadoDeLaPuerta(prompt, ultimoExperimento, etiquetaDelBorrador);

  /* Autoguardado: solo cuando lo escrito difiere de lo guardado y es publicable
     como texto. El servidor exige 10 caracteres como mínimo. */
  const { mutate: guardarBorrador } = guardar;
  useEffect(() => {
    if (editado === null || editado === textoBase || editado.trim().length < 10) return;
    const temporizador = setTimeout(
      () => guardarBorrador({ funcionId: prompt.id, texto: editado }),
      RETRASO_DEL_AUTOGUARDADO,
    );

    return () => clearTimeout(temporizador);
  }, [editado, textoBase, prompt.id, guardarBorrador]);

  const publicadas = lista.filter((r) => prompt.revision_en_uso_id !== null && r.id <= prompt.revision_en_uso_id);

  function publicarBorrador() {
    setErrorDePublicacion(null);
    publicar.mutate(prompt.id, {
      onSuccess: () => {
        setEditado(null);
        setVersion(BORRADOR);
        toast.success(`${etiquetaDelBorrador ?? "El borrador"} publicada.`);
      },
      onError: (error) => setErrorDePublicacion(getApiErrorMessage(error, "No pudimos publicar el borrador.")),
    });
  }

  const meta = [
    `${prompt.version_publicada ?? "Sin versión"} publicada`,
    `modelo ${prompt.modelo}`,
    `temperatura ${prompt.temperatura === null ? "—" : prompt.temperatura.toLocaleString("es-CL")}`,
    `usado en ${prompt.uso.informes} ${prompt.uso.informes === 1 ? "informe" : "informes"}`,
  ].join(" · ");

  const etiquetaPublicar = prompt.tiene_borrador ? `Publicar como ${etiquetaDelBorrador ?? "la versión siguiente"}` : `Publicada ${prompt.version_publicada ?? ""}`.trim();

  return (
    <Card variant="marcada" className="gap-3.5 rounded-[1.25rem] px-[1.375rem] py-[1.125rem]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="rotulo-seccion">{prompt.fase}</p>
          <h3 className="text-ink text-[1.125rem] font-extrabold tracking-[-0.02em]">{prompt.nombre}</h3>
          <p className="text-ink-muted mt-0.5 font-mono text-[0.71875rem] font-semibold">{meta}</p>
        </div>
        {prompt.admite_experimentos ? (
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" className="rounded-[0.75rem] px-3.5 text-[0.78125rem] font-bold" onClick={() => onProbar(prompt.id)}>
              Probar borrador
            </Button>
            <Button
              type="button"
              variant="fuerte"
              className="rounded-[0.75rem] px-3.5 text-[0.78125rem] font-bold"
              disabled={!puerta.puedePublicar || publicar.isPending}
              title={puerta.puedePublicar ? undefined : "Requiere un experimento aprobado"}
              onClick={publicarBorrador}
            >
              {etiquetaPublicar}
            </Button>
          </div>
        ) : null}
      </div>

      <BandaDeLaPuerta tipo={puerta.tipo} mensaje={puerta.mensaje} />
      {errorDePublicacion ? (
        <p role="alert" className="text-meta text-danger-texto">
          {errorDePublicacion}
        </p>
      ) : null}

      {prompt.admite_experimentos ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-1.5">
            {prompt.tiene_borrador ? (
              <PildoraDeVersion
                seleccionada={esBorrador}
                titulo="Borrador"
                detalle={prompt.tiene_borrador ? "sin publicar" : `igual a ${prompt.version_publicada}`}
                onClick={() => setVersion(BORRADOR)}
              />
            ) : null}
            {publicadas.map((r) => (
              <PildoraDeVersion
                key={r.id}
                seleccionada={version === r.id}
                titulo={etiquetaDeRevision(lista, r.id) ?? "v?"}
                detalle={`${r.id === prompt.revision_en_uso_id ? "Publicada · " : ""}${r.creada_en ? formatearFecha(r.creada_en).slice(0, 5) : ""}`}
                onClick={() => {
                  setVersion(r.id);
                  setVerCambios(false);
                }}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {esBorrador && prompt.tiene_borrador ? (
              <>
                <Switch aria-label={`Ver cambios contra ${prompt.version_publicada}`} checked={verCambios} onCheckedChange={setVerCambios} className="h-[1.125rem] w-[1.875rem] [&>span]:size-3.5 data-[state=checked]:bg-primary data-[state=checked]:[&>span]:translate-x-[0.8125rem]" />
                <span className="text-ink-secundario text-[0.75rem] font-bold">Ver cambios contra {prompt.version_publicada}</span>
              </>
            ) : !esBorrador ? (
              <span className="text-ink-secundario text-[0.75rem] font-bold">Solo lectura</span>
            ) : null}
          </div>
        </div>
      ) : null}

      <Editor
        prompt={prompt}
        esBorrador={esBorrador}
        verCambios={verCambios}
        texto={texto}
        enUso={enUso.data?.cuerpo ?? null}
        publicada={publicadaElegida.data?.cuerpo ?? null}
        versionPublicada={prompt.version_publicada}
        onCambio={setEditado}
      />

      <div className="text-ink-medio flex flex-wrap items-center justify-between gap-2 text-[0.71875rem] font-semibold">
        <span>
          {!prompt.admite_experimentos ? (
            <>
              Este prompt se edita desde el <Link to="/admin/ia" className="text-info-soft-foreground underline">Mantenedor de IA</Link>.
            </>
          ) : esBorrador ? (
            verCambios ? (
              "Apaga «Ver cambios» para editar el borrador."
            ) : (
              "Las versiones publicadas no se modifican: se edita el borrador."
            )
          ) : (
            "Versión publicada · no se puede modificar."
          )}
        </span>
        {editable ? (
          guardar.isPending ? (
            <span>Guardando…</span>
          ) : guardar.isError ? (
            <span className="text-danger-texto font-bold">No pudimos guardar el borrador.</span>
          ) : prompt.tiene_borrador || guardar.isSuccess ? (
            <span className="text-success-soft-foreground font-bold">Borrador guardado automáticamente</span>
          ) : null
        ) : null}
      </div>
    </Card>
  );
}

function BandaDeLaPuerta({ tipo, mensaje }: { tipo: string; mensaje: string }) {
  const tono =
    tipo === "aprobado" || tipo === "publicada"
      ? { fondo: "bg-success-soft text-success-soft-foreground", punto: "bg-primary" }
      : tipo === "pendiente"
        ? { fondo: "bg-warning-soft text-warning-texto", punto: "bg-[#e0a63a]" }
        : { fondo: "bg-divisor text-ink-secundario", punto: "bg-ink-ghost" };

  return (
    <div data-slot="banda-de-la-puerta" data-tipo={tipo} className={cn("flex items-center gap-2.5 rounded-xl px-3 py-[0.5625rem] text-[0.78125rem] font-bold", tono.fondo)}>
      <span aria-hidden className={cn("size-2 shrink-0 rounded-full", tono.punto)} />
      {mensaje}
    </div>
  );
}

function PildoraDeVersion({
  titulo,
  detalle,
  seleccionada,
  onClick,
}: {
  titulo: string;
  detalle: string;
  seleccionada: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={seleccionada}
      className={cn(
        "bg-card inline-flex h-8 items-center gap-1.5 rounded-[0.625rem] border-[1.5px] border-[#e4e5e2] px-[0.6875rem] text-[0.75rem] font-bold transition-colors",
        seleccionada ? "border-ink bg-ink text-background" : "hover:border-ink-ghost",
      )}
    >
      <span className="font-mono">{titulo}</span>
      <span className={cn("text-[0.65625rem] font-semibold", seleccionada ? "text-[#c9cbce]" : "text-ink-medio")}>{detalle}</span>
    </button>
  );
}

function Editor({
  prompt,
  esBorrador,
  verCambios,
  texto,
  enUso,
  publicada,
  versionPublicada,
  onCambio,
}: {
  prompt: PromptDelLaboratorio;
  esBorrador: boolean;
  verCambios: boolean;
  texto: string;
  enUso: string | null;
  publicada: string | null;
  versionPublicada: string | null;
  onCambio: (texto: string) => void;
}) {
  const caja = "min-h-72 rounded-xl border-[1.5px] px-4 py-3.5 font-mono text-[0.78125rem] leading-[1.7]";

  if (esBorrador && verCambios) {
    const lineas = diffDeLineas(enUso ?? "", texto);

    return (
      <section aria-label={`Cambios contra ${versionPublicada}`} className={cn(caja, "border-border bg-card px-0")}>
        {lineas.map((l, i) => (
          <div
            key={i}
            data-linea={l.tipo}
            className={cn(
              "flex gap-1.5 px-4 whitespace-pre-wrap",
              l.tipo === "agregada" && "bg-success-soft text-success-soft-foreground",
              l.tipo === "quitada" && "bg-danger-soft text-danger-texto line-through",
            )}
          >
            <span aria-hidden className="w-3 shrink-0 select-none">
              {l.tipo === "agregada" ? "+" : l.tipo === "quitada" ? "−" : ""}
            </span>
            <span>{l.texto === "" ? " " : l.texto}</span>
          </div>
        ))}
      </section>
    );
  }

  if (!esBorrador || !prompt.admite_experimentos) {
    const cuerpo = esBorrador ? texto : (publicada ?? "");

    return (
      <section aria-label="Texto del prompt" className={cn(caja, "border-border bg-card whitespace-pre-wrap")}>
        {cuerpo}
      </section>
    );
  }

  return (
    <Textarea
      aria-label="Texto del prompt"
      value={texto}
      onChange={(e) => onCambio(e.target.value)}
      className={cn(caja, "border-input bg-[#fcfcfb] field-sizing-fixed resize-y")}
    />
  );
}
